import { Tabs, Row } from 'antd';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { LoanActions, SettingsActions } from '@portal/actions';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import LoanAdvantages from '@portal/components/LoanAdvantages/LoanAdvantages';
import LoanContract from '@portal/components/LoanContract/LoanContract';
import LoanSimulation from '@portal/components/LoanSimulation/LoanSimulation';
import LoanExternalSimulation from '@portal/components/LoanExternalSimulation/LoanExternalSimulation';
import LoanWarranty from '@portal/components/LoanWarranty/LoanWarranty';
import PanelContentBreadcrumb from '@portal/components/PanelContentBreadcrumb/PanelContentBreadcrumb';
import PanelContentHeader from '@portal/components/PanelContentHeader/PanelContentHeader';

import { getRouteStackPath } from '@portal/config/routes';
import usePrevious from '@portal/hooks/usePrevious';
import useReduxState from '@portal/hooks/useReduxState';
import {
  PaymentAccountStatus,
  PaymentAccountType,
} from '@portal/models/enumerators/paymentAccount';
import { translate } from '@portal/services/i18n';
import * as MessageService from '@portal/services/message';
import { LoanSimulationTab } from '@portal/models/enumerators/loans';
import NavigationService from '@portal/services/navigation';
import MessageModal from '@portal/components/MessageModal/MessageModal';
import { useNavigation } from '@portal/hooks/useNavigation';

const SimulateLoan = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation<{ onlyWarranty?: boolean }>();

  const [tabKey, setTabKey] = useState(
    navigate?.location?.state?.onlyWarranty ? 6 : 1
  );
  const [advanceButtonText, setAdvanceButtonText] = useState('');
  const [loanAmount, setLoanAmount] = useState(0);
  const [alertText, setAlertText] = useState('');
  const [dueDate, setDueDate] = useState<DateTime | null>(null);
  const [contractStartDate, setContractStartDate] = useState<DateTime | null>(
    null
  );
  const [minDueDate, setMinDueDate] = useState<DateTime | null>(null);
  const [contractChecks, setContractChecks] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState<number | null>(
    null
  );
  const [selectedLack, setSelectedLack] = useState<number | undefined>();
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [hasWarranty, setHasWarranty] = useState(false);
  const [warranties, setWarranties] = useState<FileList | null>(null);
  const [bankAgency, setBankAgency] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankAccountDigit, setBankAccountDigit] = useState('');
  const [bankCode, setBankCode] = useState('');

  const {
    loan: {
      loan,
      preApprovedLoans,
      simulations,
      externalSimulation,
      success,
      error,
    },
    settings: { loanSettings },
    user: {
      me: {
        account: { paymentAccounts },
      },
    },
  } = useReduxState();

  const prevTabKey = usePrevious(tabKey);
  const prevExternalSimulation = usePrevious(externalSimulation);

  const getDisabled = (): boolean => {
    switch (tabKey) {
      case LoanSimulationTab.FINANCIAL_DATA:
        if (
          !dueDate ||
          !contractStartDate ||
          !selectedInstallment ||
          (simulations?.maxLacks && !selectedLack)
        ) {
          return true;
        }

        return dueDate < contractStartDate || !!alertText || !dueDate.isValid;

      case LoanSimulationTab.CONTRACT:
        return !contractChecks || !selectedInstallment;

      case LoanSimulationTab.CONFIRMATION:
        if (!bankAgency || !bankAccount || !bankAccountDigit || !bankCode)
          return true;

        return !confirmed;

      case LoanSimulationTab.WARRANTY:
        return (
          ((hasWarranty && !warranties) ||
            (warranties && warranties.length)) === 0
        );
    }

    return false;
  };

  const showConfirmModal = () => {
    setIsConfirmModalVisible(true);
  };

  const handleConfirmLoan = () => {
    if (preApprovedLoans && preApprovedLoans.length > 0 && externalSimulation) {
      dispatch(
        LoanActions.createLoan(preApprovedLoans[0].id, externalSimulation, {
          issuersBranch: bankAgency,
          issuersBank: bankCode,
          issuersAccount: bankAccount + bankAccountDigit,
        })
      );
    }
    setIsConfirmModalVisible(false);
  };

  useEffect(() => {
    if (
      !paymentAccounts.some(
        (paymentAccount) =>
          paymentAccount.type === PaymentAccountType.LOAN &&
          paymentAccount.status === PaymentAccountStatus.APPROVED
      )
    ) {
      navigate(getRouteStackPath('LOAN', 'REGISTER'));
    }
  }, [navigate, paymentAccounts]);

  useEffect(() => {
    if (tabKey === LoanSimulationTab.CONFIRMATION) {
      setAdvanceButtonText(translate('PAGES.LOAN.SIMULATE.CONFIRM_CONTRACT'));
    } else if (tabKey === LoanSimulationTab.WARRANTY && !hasWarranty) {
      setAdvanceButtonText(translate('COMPONENTS.ADVANCED_BUTTON.FINISH'));
    } else {
      setAdvanceButtonText(translate('COMPONENTS.ADVANCED_BUTTON.ADVANCE'));
    }
  }, [tabKey, hasWarranty]);

  useEffect(() => {
    if (preApprovedLoans) {
      if (preApprovedLoans[0]) {
        setLoanAmount(preApprovedLoans[0].maxAmount);
      }
    } else {
      dispatch(LoanActions.getPreApprovedLoans());
      setTabKey(LoanSimulationTab.ADVANTAGES);
    }
  }, [dispatch, preApprovedLoans]);

  useEffect(() => {
    if (prevTabKey && tabKey < prevTabKey) {
      switch (prevTabKey) {
        case LoanSimulationTab.CONFIRMATION:
          dispatch(LoanActions.setLoanExternalSimulation(null));
          break;

        default:
          break;
      }
    }
  }, [dispatch, prevTabKey, tabKey]);

  useEffect(() => {
    if (error) {
      MessageService.error(`LOAN.${error.message}`, { originalError: error });
    }
  }, [dispatch, error]);

  useEffect(() => {
    if (simulations) {
      setContractStartDate(DateTime.fromISO(simulations.contractStartDate));
      setTabKey(LoanSimulationTab.FINANCIAL_DATA);
    }
  }, [simulations]);

  useEffect(() => {
    if (externalSimulation && !prevExternalSimulation) {
      setTabKey(LoanSimulationTab.CONFIRMATION);
    }
  }, [externalSimulation, prevExternalSimulation]);

  useEffect(() => {
    if (success === 'CREATE') {
      dispatch(LoanActions.setLoanSuccess(null));
      setTabKey(LoanSimulationTab.WARRANTY);
    } else if (success === 'LOAN_FINALIZE') {
      MessageService.success(`PAGES.LOAN.SUCCESS_MESSAGES.${success}`);
      dispatch(LoanActions.setLoanSuccess(null));
      navigate(getRouteStackPath('LOAN', 'LOAN_PAGE'));
    }
  }, [dispatch, hasWarranty, navigate, success]);

  useEffect(() => {
    if (loanSettings) {
      if (preApprovedLoans && preApprovedLoans.length > 0) {
        if (preApprovedLoans[0].requestWarranty === null) {
          setHasWarranty(loanSettings.hasWarranty);
        } else {
          setHasWarranty(preApprovedLoans[0].requestWarranty);
        }
      }
    } else {
      dispatch(SettingsActions.getLoanSettings());
    }
  }, [dispatch, loanSettings, preApprovedLoans]);

  useEffect(() => {
    if (preApprovedLoans && preApprovedLoans.length > 0) {
      setDueDate(DateTime.fromISO(preApprovedLoans[0].minDate));
      setMinDueDate(DateTime.fromISO(preApprovedLoans[0].minDate));
    }
  }, [preApprovedLoans]);

  useEffect(() => {
    if (navigate?.location?.state?.onlyWarranty) {
      setTabKey(LoanSimulationTab.WARRANTY);
    }
  }, [navigate?.location?.state?.onlyWarranty]);

  const handleAdvance = () => {
    switch (tabKey) {
      case LoanSimulationTab.ADVANTAGES:
        if (preApprovedLoans && preApprovedLoans.length > 0) {
          dispatch(
            LoanActions.getSimulation(preApprovedLoans[0].id, loanAmount)
          );
        }
        break;

      case LoanSimulationTab.CONTRACT:
        if (
          preApprovedLoans &&
          preApprovedLoans.length > 0 &&
          selectedInstallment &&
          dueDate &&
          contractStartDate
        ) {
          dispatch(
            LoanActions.getExternalSimulation(preApprovedLoans[0].id, {
              amount: loanAmount,
              installments: selectedInstallment,
              dueDate: dueDate.toISODate(),
              contractStartDate: contractStartDate.toISODate(),
              termsOfUse: loanSettings?.termsOfUse ?? '',
              lack: selectedLack,
            })
          );
        }
        break;

      case LoanSimulationTab.CONFIRMATION:
        showConfirmModal();
        break;

      case LoanSimulationTab.WARRANTY:
        if (!hasWarranty) {
          NavigationService.navigate(getRouteStackPath('LOAN', 'LOAN_PAGE'));
        }
        if (warranties && loan && hasWarranty) {
          dispatch(LoanActions.uploadWarranties(loan.id, warranties));
        }
        break;

      default:
        setTabKey(tabKey + 1);
    }
  };

  const handleBack = () => {
    if (
      navigate?.location?.state?.onlyWarranty ||
      tabKey === LoanSimulationTab.WARRANTY
    ) {
      return;
    }

    setTabKey(tabKey - 1);
  };

  const renderTabTitle = (title: string) => (
    <p className="simulate-loan__tab-title">{title}</p>
  );

  const AdvanceButton = () => (
    <Row className="simulate-loan__buttons">
      {tabKey > 1 && tabKey !== LoanSimulationTab.WARRANTY && (
        <div className="simulate-loan__buttons__button">
          <AdvancedButton
            text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
            variant="text"
            onClick={handleBack}
          />
        </div>
      )}
      <AdvancedButton
        text={advanceButtonText}
        onClick={handleAdvance}
        disabled={getDisabled()}
      />
    </Row>
  );

  if (
    (!preApprovedLoans || preApprovedLoans.length === 0) &&
    !navigate?.location?.state?.onlyWarranty
  ) {
    return <></>;
  }

  return (
    <div className="simulate-loan">
      <PanelContentBreadcrumb
        items={[
          {
            active: true,
            title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
            url: `${getRouteStackPath('HOME', 'HOME_PAGE')}`,
          },
          {
            active: true,
            title: translate('PAGES.LOAN.TITLE'),
            url: `${getRouteStackPath('LOAN', 'LOAN_PAGE')}`,
          },
          {
            active: false,
            title: translate('PAGES.LOAN.SIMULATE.TITLE'),
          },
        ]}
      />
      <PanelContentHeader
        pageTitle={translate('PAGES.LOAN.TITLE')}
        pageDescription={translate('PAGES.LOAN.LOAN_PAGE.DESCRIPTION')}
      />
      <div className="simulate-loan__container">
        <Tabs
          defaultActiveKey="1"
          activeKey={tabKey.toString()}
          onChange={(value) => setTabKey(parseInt(value, 10))}
        >
          <Tabs.TabPane
            tab={renderTabTitle(
              translate('PAGES.LOAN.SIMULATE.TABS.ADVANTAGES')
            )}
            key={LoanSimulationTab.ADVANTAGES}
            disabled={
              navigate?.location?.state?.onlyWarranty ||
              tabKey === LoanSimulationTab.WARRANTY
            }
          >
            <LoanAdvantages />
          </Tabs.TabPane>
          <Tabs.TabPane
            tab={renderTabTitle(
              translate('PAGES.LOAN.SIMULATE.TABS.FINANCIAL_DATA')
            )}
            key={LoanSimulationTab.FINANCIAL_DATA}
            disabled={
              navigate?.location?.state?.onlyWarranty ||
              !simulations ||
              tabKey === LoanSimulationTab.WARRANTY
            }
          >
            {preApprovedLoans && preApprovedLoans.length > 0 ? (
              <LoanSimulation
                loanAmount={loanAmount}
                setLoanAmount={setLoanAmount}
                alertText={alertText}
                setAlertText={setAlertText}
                maxAmount={preApprovedLoans[0].maxAmount}
                minAmount={preApprovedLoans[0].minAmount}
                dueDate={dueDate}
                setDueDate={setDueDate}
                minDueDate={minDueDate}
                selectedInstallment={selectedInstallment}
                setSelectedInstallment={setSelectedInstallment}
                contractStartDate={contractStartDate}
                setContractStartDate={setContractStartDate}
                selectedLack={selectedLack}
                setSelectedLack={setSelectedLack}
              />
            ) : null}
          </Tabs.TabPane>
          <Tabs.TabPane
            tab={renderTabTitle(translate('PAGES.LOAN.SIMULATE.TABS.CONTRACT'))}
            key={LoanSimulationTab.CONTRACT}
            disabled={
              loanAmount === 0 ||
              !dueDate ||
              navigate?.location?.state?.onlyWarranty ||
              !simulations ||
              !selectedInstallment ||
              tabKey === LoanSimulationTab.WARRANTY
            }
          >
            <LoanContract
              contractChecks={contractChecks}
              setContractChecks={setContractChecks}
              termsOfUse={loanSettings?.termsOfUse ?? ''}
            />
          </Tabs.TabPane>
          <Tabs.TabPane
            tab={renderTabTitle(
              translate('PAGES.LOAN.SIMULATE.TABS.CONFIRMATION')
            )}
            key={LoanSimulationTab.CONFIRMATION}
            disabled={
              !externalSimulation || tabKey === LoanSimulationTab.WARRANTY
            }
          >
            {externalSimulation ? (
              <LoanExternalSimulation
                externalSimulation={externalSimulation}
                confirmed={confirmed}
                setConfirmed={setConfirmed}
                bankAgency={bankAgency}
                setBankAgency={setBankAgency}
                bankAccount={bankAccount}
                setBankAccount={setBankAccount}
                bankAccountDigit={bankAccountDigit}
                setBankAccountDigit={setBankAccountDigit}
                bankCode={bankCode}
                setBankCode={setBankCode}
              />
            ) : null}
          </Tabs.TabPane>
          <Tabs.TabPane
            tab={renderTabTitle(translate('PAGES.LOAN.SIMULATE.TABS.WARRANTY'))}
            key={LoanSimulationTab.WARRANTY}
            disabled
          >
            <LoanWarranty
              hasWarranty={hasWarranty}
              warranties={warranties}
              setWarranties={setWarranties}
            />
          </Tabs.TabPane>
        </Tabs>
        <div className="simulate-loan__container__button">
          <AdvanceButton />
        </div>
        <MessageModal
          visible={isConfirmModalVisible}
          onConfirm={handleConfirmLoan}
          onCancel={() => setIsConfirmModalVisible(false)}
          setVisible={setIsConfirmModalVisible}
          buttonOkText={translate('SHARED.FINISH')}
          buttonCancelText={translate('SHARED.CANCEL')}
          title={translate(
            'PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.FINAL_CREDIT_ALERT'
          )}
          message={translate(
            'PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.FINAL_CREDIT_ALERT_TEXT'
          )}
        />
      </div>
    </div>
  );
};

export default SimulateLoan;
